/**
 * Modular date range configuration system for admin dashboard
 * Supports presets like "last month", "this month", "last 30 days", etc.
 */

export type DateRangePresetId =
  | "last7Days"
  | "last30Days"
  | "last90Days"
  | "last365Days"
  | "lastMonth"
  | "thisMonth"

export type DateRange =
  | { type: "preset"; presetId: DateRangePresetId }
  | { type: "custom"; startDate: string; endDate: string }
  | { type: "days"; days: number } // backward compatibility

export type DateRangePreset = {
  id: DateRangePresetId
  labelKey: string
  getDates: () => { startDate: string; endDate: string }
}

const MS_IN_DAY = 24 * 60 * 60 * 1000

function startOfTodayUtc(): Date {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

function endOfTodayUtc(): Date {
  const now = new Date()
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999)
  )
}

function shiftUtc(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_IN_DAY)
}

/**
 * Calculate the start and end dates for a date range preset.
 * All ranges are calculated using UTC boundaries to avoid timezone skew when serialized to ISO strings.
 */
function getPresetDates(presetId: DateRangePresetId): { startDate: string; endDate: string } {
  const todayStartUtc = startOfTodayUtc()
  let startDate = new Date(todayStartUtc)
  let endDate = endOfTodayUtc()

  switch (presetId) {
    case "last7Days": {
      startDate = shiftUtc(todayStartUtc, -6) // Include today, so 7 days total
      break
    }
    case "last30Days": {
      startDate = shiftUtc(todayStartUtc, -29) // Include today, so 30 days total
      break
    }
    case "last90Days": {
      startDate = shiftUtc(todayStartUtc, -89) // Include today, so 90 days total
      break
    }
    case "last365Days": {
      startDate = shiftUtc(todayStartUtc, -364) // Include today, so 365 days total
      break
    }
    case "lastMonth": {
      // First day of last month at 00:00 UTC
      const now = new Date()
      startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
      // Last day of last month at 23:59:59.999 UTC
      endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59, 999))
      break
    }
    case "thisMonth": {
      const now = new Date()
      startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      endDate = endOfTodayUtc()
      break
    }
    default: {
      startDate = shiftUtc(todayStartUtc, -29)
    }
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  }
}

/**
 * Resolve a date range to start and end dates
 */
export function resolveDateRange(dateRange: DateRange): { startDate: string; endDate: string } {
  if (dateRange.type === "preset") {
    return getPresetDates(dateRange.presetId)
  }
  if (dateRange.type === "custom") {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }
  }
  // Backward compatibility: convert days to date range
  const todayStartUtc = startOfTodayUtc()
  const endDate = endOfTodayUtc()
  const startDate = shiftUtc(todayStartUtc, -(dateRange.days - 1)) // Include today
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  }
}

/**
 * Get all available date range presets
 */
export function getDateRangePresets(): DateRangePreset[] {
  return [
    {
      id: "last7Days",
      labelKey: "adminDashboard.page.periodSelector.last7Days",
      getDates: () => getPresetDates("last7Days"),
    },
    {
      id: "last30Days",
      labelKey: "adminDashboard.page.periodSelector.last30Days",
      getDates: () => getPresetDates("last30Days"),
    },
    {
      id: "last90Days",
      labelKey: "adminDashboard.page.periodSelector.last90Days",
      getDates: () => getPresetDates("last90Days"),
    },
    {
      id: "last365Days",
      labelKey: "adminDashboard.page.periodSelector.last365Days",
      getDates: () => getPresetDates("last365Days"),
    },
    {
      id: "lastMonth",
      labelKey: "adminDashboard.page.periodSelector.lastMonth",
      getDates: () => getPresetDates("lastMonth"),
    },
    {
      id: "thisMonth",
      labelKey: "adminDashboard.page.periodSelector.thisMonth",
      getDates: () => getPresetDates("thisMonth"),
    },
  ]
}

/**
 * Get a preset by ID
 */
export function getPresetById(presetId: DateRangePresetId): DateRangePreset | undefined {
  return getDateRangePresets().find((preset) => preset.id === presetId)
}

/**
 * Calculate approximate number of days for display purposes
 * (used for chart formatting and other UI elements)
 */
export function getApproximateDays(dateRange: DateRange): number {
  if (dateRange.type === "days") {
    return dateRange.days
  }
  const { startDate, endDate } = resolveDateRange(dateRange)
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays // Already inclusive - ceil rounds up partial days
}
